/* eslint-disable no-shadow */
import { storefrontApiClient as client } from './storefrontApiClient';
import { digestResponse, handleUserErrors } from './utils';
import {
	customer,
	customerAccessTokenCreateWithMultipass,
	customerAccessTokenRenew,
	customerUpdate,
	customerAddressCreate,
	customerAddressUpdate,
	customerAddressDelete,
	customerDefaultAddressUpdate,
	customerOrders,
	cart,
	cartCreate,
	cartLinesAdd,
	cartLinesRemove,
	cartLinesUpdate,
	getProductAvailabilityById,
} from './queries';

export {
	getCollectionByHandle,
	getProductsByHandle,
	getProductsById,
} from './customFunctions';

/**
 * @param {string} multipassToken - Shopify multipass token
 * @returns Promise<object>
 */
export const createCustomerTokenWithMultipass = async multipassToken => {
	const variables = { multipassToken };
	return client.request(customerAccessTokenCreateWithMultipass, { variables })
	.then(digestResponse)
	.then(({data}) => {
		const {
			customerAccessToken,
			customerUserErrors
		} = data.customerAccessTokenCreateWithMultipass || {};
		handleUserErrors(customerUserErrors);
		return customerAccessToken || {};
	});
};

/**
 * @param {string} accessToken - Shopify customer access token
 * @returns Promise<object>
 */
export const renewCustomerToken = async accessToken => {
	const variables = { accessToken };
	return client.request(customerAccessTokenRenew, { variables })
	.then(digestResponse)
	.then(({data}) => {
		const { customerAccessToken, userErrors } = data.customerAccessTokenRenew || {};
		handleUserErrors(userErrors);
		return customerAccessToken || {};
	});
};

/**
 * @param {object} customerAccessToken
 * @param {string} customerAccessToken.accessToken
 * @param {string} [customerAccessToken.expiresAt]
 * @param {boolean} [shouldForce]
 * @returns Promise<object>
 */
export const refreshToken = (customerAccessToken, shouldForce = false) => {
	const { accessToken, expiresAt = null } = customerAccessToken;

	// Shopify Customer access tokens expire after 12 days
	const sixDays = 1000 * 60 * 60 * 24 * 6;
	const sixDaysDt = new Date().getTime() + sixDays;
	const expiresAtDt = new Date(expiresAt).getTime();

	if (expiresAtDt > sixDaysDt && !shouldForce) {
		return Promise.resolve(customerAccessToken);
	}

	return renewCustomerToken(accessToken);
};

/**
 * @param {string} accessToken - Shopify customer access token
 * @param {object} customerInput - Shopify customer updates
 * @returns Promise<object>
 */
export const updateCustomer = async (accessToken, customerInput) => {
	const variables = { customerInput, accessToken };
	return client.request(customerUpdate, { variables })
	.then(digestResponse)
	.then(({data}) => {
		const {
			customer,
			customerAccessToken,
			customerUserErrors,
		} = data.customerUpdate || {};
		handleUserErrors(customerUserErrors);
		return {
			customer,
			customerAccessToken,
		};
	});
};

/**
 * @param {string} accessToken - Shopify customer access token
 * @returns Promise<object>
 */
export const getCustomer = async accessToken => {
	const variables = { accessToken };
	return client.request(customer, { variables })
	.then(digestResponse)
	.then(({data}) => {
		const {
			customer,
			customerUserErrors,
		} = data || {};
		handleUserErrors(customerUserErrors);
		return customer;
	});
};

/**
 * @param {string} accessToken - Shopify customer access token
 * @returns Promise<object[]>
 */
export const getCustomerOrders = async accessToken => {
	const variables = { accessToken };
	return client.request(customerOrders, { variables })
	.then(digestResponse)
	.then(({data}) => {
		const {
			customer,
			customerUserErrors,
		} = data.customer || {};
		handleUserErrors(customerUserErrors);
		return customer.orders;
	});
};

/**
 * @param {string} accessToken - Shopify customer access token
 * @param {object} address - Shopify address data
 * @returns Promise<object>
 */
export const createAddress = async (accessToken, address) => {
	const variables = { accessToken, address };
	return client.request(customerAddressCreate, { variables })
		.then(digestResponse)
		.then(({data}) => {
		const {
			customerAddress,
			customerUserErrors,
		} = data.customerAddressCreate || {};
		handleUserErrors(customerUserErrors);
		return customerAddress;
	});
};

/**
 * @param {string} accessToken - Shopify customer access token
 * @param {string} id - Shopify address ID
 * @param {object} address - Shopify address data
 * @returns Promise<object>
 */
export const updateAddress = async (accessToken, id, address) => {
	const variables = { accessToken, address, id };
	return client.request(customerAddressUpdate, { variables })
	.then(digestResponse)
	.then(({data}) => {
		const {
			customerAddress,
			customerUserErrors,
		} = data.customerAddressUpdate || {};
		handleUserErrors(customerUserErrors);
		return customerAddress;
	});
};

/**
 * @param {string} accessToken - Shopify customer access token
 * @param {string} id - Shopify address ID
 * @returns Promise<string>
 */
export const deleteAddress = async (accessToken, id) => {
	const variables = { accessToken, id };
	return client.request(customerAddressDelete, { variables })
	.then(digestResponse)
	.then(({data}) => {
	const {
		deletedCustomerAddressId,
		customerUserErrors,
	} = data.customerAddressDelete || {};
		handleUserErrors(customerUserErrors);
		return deletedCustomerAddressId || '';
	});
};

/**
 * @param {string} accessToken - Shopify customer access token
 * @param {string} id - Shopify address ID
 * @returns Promise<object>
 */
export const updateDefaultAddress = async (accessToken, id) => {
	const variables = { accessToken, id };
	return client.request(customerDefaultAddressUpdate, { variables })
	.then(digestResponse)
	.then(({data}) => {
	const { customer, customerUserErrors } =
		data.customerDefaultAddressUpdate || {};
		handleUserErrors(customerUserErrors);
		return customer || {};
	});
};

/**
 * @param {string} id - Shopify cart ID
 * @returns Promise<object>
 */
export const getCart = async (id) => {
	const variables = { id };
	return client.request(cart, { variables })
	.then(digestResponse)
	.then(({data, errors}) => {
		const {
			cart,
		} = data || {};
		handleUserErrors(errors);
		return cart || {};
	});
};

/**
 * @param {object} [input] - Shopify cart create input
 * @returns Promise<object>
 */
export const createCart = async (input = {}) => {
	const variables = { input };
	return client.request(cartCreate, { variables })
	.then(digestResponse)
	.then(({data}) => {
		const {
			cart,
			userErrors,
		} = data.cartCreate || {};
		handleUserErrors(userErrors);
		return cart || {};
	});
};

/**
 * @param {string} [cartId] - Shopify cart ID
 * @param {object[]} [lines] - Merchandise lines
 * @returns Promise<object>
 */
export const addCartLines = async (cartId, lines) => {
	const variables = { cartId, lines };
	return client.request(cartLinesAdd, { variables })
	.then(digestResponse)
	.then(({data}) => {
		const {
			cart,
			userErrors,
		} = data.cartLinesAdd || {};
		handleUserErrors(userErrors);
		return cart || {};
	});
};

/**
 * @param {string} [cartId] - Shopify cart ID
 * @param {string[]} [lineIds] - Merchandise line IDs
 * @returns Promise<object>
 */
export const removeCartLines = async (cartId, lineIds) => {
	const variables = { cartId, lineIds };
	return client.request(cartLinesRemove, { variables })
	.then(digestResponse)
	.then(({data}) => {
		const {
			cart,
			userErrors,
		} = data.cartLinesRemove || {};
		handleUserErrors(userErrors);
		return cart || {};
	});
};

/**
 * @param {string} [cartId] - Shopify cart ID
 * @param {object[]} [lines] - Merchandise lines
 * @returns Promise<object>
 */
export const updateCartLines = async (cartId, lines) => {
	const variables = { cartId, lines };
	return client.request(cartLinesUpdate, { variables })
	.then(digestResponse)
	.then(({data}) => {
		const {
			cart,
			userErrors
		} = data.cartLinesUpdate || {};
		handleUserErrors(userErrors);
		return cart || {};
	});
};

/**
 * @param {string} [productId] - Shopify Product ID
 * @returns Promise<object>
 */
export const getProductAvailability = async (id) => {
	const variables = { id };
	return client.request(getProductAvailabilityById, { variables })
	.then(digestResponse)
	.then(({data, errors}) => {
		const {
			product
		} = data || {};
		handleUserErrors(errors);
		return product || {};
	});
};
